import type { MouseEvent } from 'react'
import { slugify } from './slug'
import type { Product } from './types'

export const PRODUCT_DETAIL_BASE = '/produto'
export const LEGACY_PRODUCT_DETAIL_BASE = '/oferta'
export const PRODUCTS_SEARCH_PATH = '/buscar'
export const LEGACY_PRODUCTS_PATH = '/produtos'
export const LEGACY_INSTITUTIONAL_PATH = '/institucional'
export const ACCOUNT_PATH_PREFIX = '/minha-conta'
export const BUYER_SHORT_ACCESS_PATH_PREFIX = '/l'
export const PUBLIC_TICKET_PATH_PREFIX = '/tk'
export const MARKETPLACE_VITRINE_DETAIL_BASE = '/anuncio'
export const MARKETPLACE_VITRINE_SELLER_BASE = '/vendedor'

const buildAccountViewPath = (segment: string) => `${ACCOUNT_PATH_PREFIX}/${segment}`
const ACCOUNT_DETAIL_SUFFIX = 'detalhes'
export const MARKETPLACE_ACCOUNT_HOME_PATH = buildAccountViewPath('marketplace')

export const VIEW_PATHS = {
  home: '/',
  access: '/acesso',
  'public-ticket': PUBLIC_TICKET_PATH_PREFIX,
  'redirect-checkout': '/redirect-checkout',
  products: PRODUCTS_SEARCH_PATH,
  qrcode: '/qrcode.php',
  'nfe-qr': '/qr',
  launches: '/lancamentos',
  just_arrived: '/acabou-de-chegar',
  'product-detail': PRODUCT_DETAIL_BASE,
  'marketplace-listing-detail': MARKETPLACE_VITRINE_DETAIL_BASE,
  'marketplace-seller': MARKETPLACE_VITRINE_SELLER_BASE,
  tracking: '/rastreamento',
  'price-table': '/tabela-de-precos',
  'price-table-margin': '/tabela-de-precos-com-margem',
  'store-network-marketplace': '/marketplace',
  'store-network-access': '/marketplace/acesso',
  cart: '/carrinho',
  quotes: buildAccountViewPath('orcamentos'),
  attendances: buildAccountViewPath('atendimentos'),
  login: '/login',
  register: '/cadastro',
  checkout: '/checkout',
  'checkout-tracking': '/checkout/acompanhamento',
  'quick-checkout': '/finalizar-compra',
  dashboard: buildAccountViewPath('resumo'),
  profile: buildAccountViewPath('dados-cadastrais'),
  'change-password': buildAccountViewPath('alterar-senha'),
  users: buildAccountViewPath('meus-usuarios'),
  business: buildAccountViewPath('meu-negocio'),
  clients: buildAccountViewPath('meus-clientes'),
  marketplace: buildAccountViewPath('marketplace/perfil'),
  'marketplace-listings': buildAccountViewPath('marketplace/anuncios'),
  'marketplace-questions': buildAccountViewPath('marketplace/perguntas'),
  'marketplace-performance': buildAccountViewPath('marketplace/desempenho'),
  finance: buildAccountViewPath('financeiro'),
  benefits: buildAccountViewPath('beneficios'),
  cashback: buildAccountViewPath('cashback'),
  addresses: buildAccountViewPath('enderecos'),
  favorites: buildAccountViewPath('favoritos'),
  orders: buildAccountViewPath('pedidos'),
  indicator: buildAccountViewPath('indicador'),
  affiliates: buildAccountViewPath('afiliados'),
  returns: buildAccountViewPath('devolucoes'),
  'return-request': '/devolucao',
  whatsapp: '/whatsapp',
  support: buildAccountViewPath('atendimento'),
  'service-evaluation': '/avalie-nosso-atendimento',
  blog: '/comunidade',
  compare: '/comparar',
  about: '/quem-somos',
  privacy: '/politicas-de-privacidade',
  terms: '/termos-de-uso',
  careers: '/trabalhe-conosco',
  'returns-policy': '/politicas-de-devolucoes',
} as const

export type AppView = keyof typeof VIEW_PATHS

export type ProductNavigationContext = {
  routeProductId?: string | null
  parentContextId?: string | null
}

export type ProductHrefOptions = Pick<ProductNavigationContext, 'parentContextId'>

export const normalizeProductRouteValue = (value?: string | null) => {
  const normalized = String(value ?? '').trim()
  if (!normalized) return null

  let decoded = normalized
  try {
    decoded = decodeURIComponent(normalized).trim()
  } catch {
    decoded = normalized
  }

  const sanitized = decoded.split(/[?#]/, 1)[0]?.trim() ?? ''
  return sanitized || null
}

export const normalizeLegacyProductDetailPathname = (pathname: string) =>
  pathname.replace(/^\/oferta(?=\/|$)/i, PRODUCT_DETAIL_BASE)

export const LEGACY_VIEW_PATH_ALIASES = {
  dashboard: ['/conta', ACCOUNT_PATH_PREFIX],
  tracking: [buildAccountViewPath('rastreamento')],
  'price-table': [`${ACCOUNT_PATH_PREFIX}/tabela-de-precos`],
  'price-table-margin': [`${ACCOUNT_PATH_PREFIX}/tabela-de-precos-com-margem`],
  quotes: ['/orcamentos'],
  attendances: ['/atendimentos'],
  finance: ['/financeiro'],
  benefits: ['/beneficios'],
  cashback: ['/cashback'],
  addresses: ['/enderecos'],
  favorites: ['/favoritos'],
  orders: ['/pedidos'],
  indicator: ['/indicador'],
  affiliates: ['/afiliados'],
  returns: ['/devolucoes'],
  whatsapp: ['/whatsapp.php'],
  support: ['/atendimento', '/suporte'],
  profile: ['/perfil'],
  'change-password': ['/alterar-senha'],
  users: ['/meus-usuarios'],
  clients: ['/meus-clientes'],
} as const satisfies Partial<Record<AppView, readonly string[]>>

export const buildProductHref = (
  product: Pick<Product, 'id' | 'name' | 'navigationTargetProductId' | 'origem' | 'marketplaceAnuncioId'>,
  options: ProductHrefOptions = {},
) => {
  if (product.origem === 'marketplace_vitrine' && product.marketplaceAnuncioId) {
    return buildMarketplaceVitrineHref(product.marketplaceAnuncioId, product.name)
  }
  const routeProductId = product.navigationTargetProductId?.trim() || product.id
  const normalizedProductId = normalizeProductRouteValue(routeProductId) || String(routeProductId ?? '').trim()
  const slugBase = slugify(product.name || '') || 'produto'
  const normalizedParentContextId = normalizeProductRouteValue(options.parentContextId)
  const shouldAppendParentContext = Boolean(
    normalizedParentContextId
    && normalizedParentContextId !== normalizedProductId
  )

  return `${PRODUCT_DETAIL_BASE}/${normalizedProductId}/${slugBase}${shouldAppendParentContext ? `?pai=${encodeURIComponent(normalizedParentContextId!)}` : ''}`
}

export const buildMarketplaceVitrineHref = (anuncioId: number, title: string) =>
  `${MARKETPLACE_VITRINE_DETAIL_BASE}/${Math.max(1, Math.trunc(anuncioId))}/${slugify(title) || 'anuncio'}`

export const buildMarketplaceVitrineSellerHref = (entidadeId: number, sellerName: string) =>
  `${MARKETPLACE_VITRINE_SELLER_BASE}/${Math.max(1, Math.trunc(entidadeId))}/${slugify(sellerName) || 'vendedor'}`

export const preserveMarketplaceVitrineDetailPath = (pathname: string) => {
  const normalized = `/${String(pathname ?? '').split('?')[0].split('#')[0].split('/').filter(Boolean).join('/')}`
  const segments = normalized.split('/').filter(Boolean)
  const anuncioId = Number.parseInt(segments[1] ?? '', 10)
  return segments[0] === MARKETPLACE_VITRINE_DETAIL_BASE.slice(1)
    && Number.isFinite(anuncioId)
    && anuncioId > 0
    ? normalized
    : null
}

export const preserveMarketplaceVitrineSellerPath = (pathname: string) => {
  const normalized = `/${String(pathname ?? '').split('?')[0].split('#')[0].split('/').filter(Boolean).join('/')}`
  const segments = normalized.split('/').filter(Boolean)
  const entidadeId = Number.parseInt(segments[1] ?? '', 10)
  return segments[0] === MARKETPLACE_VITRINE_SELLER_BASE.slice(1)
    && Number.isFinite(entidadeId)
    && entidadeId > 0
    ? normalized
    : null
}

export const buildCategoryHref = (slugs: string[]) => {
  const encoded = slugs.filter(Boolean).map((slug) => encodeURIComponent(slug))
  if (encoded.length === 0) return VIEW_PATHS.home
  return `/${encoded.join('/')}`
}

export const buildAccessPath = (ticket: string) =>
  `${VIEW_PATHS.access}/${encodeURIComponent(ticket.trim())}`

export const buildShortAccessPath = (code: string) =>
  `${BUYER_SHORT_ACCESS_PATH_PREFIX}/${encodeURIComponent(code.trim())}`

export const buildCheckoutTrackingPath = (code: string) =>
  `${VIEW_PATHS['checkout-tracking']}/${encodeURIComponent(code.trim())}`

const buildAccountDetailToken = (entityId: number, detailKey?: string | number | null) => {
  const normalizedId = Math.max(1, Math.trunc(entityId))
  const normalizedIdText = String(normalizedId)
  const slugSource = typeof detailKey === 'number'
    ? String(Math.trunc(detailKey))
    : typeof detailKey === 'string'
      ? detailKey.trim()
      : ''
  const normalizedSlug = slugify(slugSource)
  return normalizedSlug && normalizedSlug !== normalizedIdText
    ? `${normalizedIdText}-${normalizedSlug}`
    : normalizedIdText
}

export const buildOrderDetailToken = (orderId: number, detailKey?: string | number | null) =>
  buildAccountDetailToken(orderId, detailKey)

export const buildQuoteDetailToken = (quoteId: number, detailKey?: string | number | null) =>
  buildAccountDetailToken(quoteId, detailKey)

export const buildAttendanceDetailToken = (attendanceId: number, detailKey?: string | number | null) =>
  buildAccountDetailToken(attendanceId, detailKey)

export const normalizeAccountDetailToken = (token: string, entityId: number) => {
  const normalizedToken = token.trim()
  const normalizedId = String(Math.max(1, Math.trunc(entityId)))
  return normalizedToken === `${normalizedId}-${normalizedId}`
    ? normalizedId
    : normalizedToken
}

export const buildOrderDetailPathFromToken = (token: string) =>
  `${VIEW_PATHS.orders}/${encodeURIComponent(token.trim())}/${ACCOUNT_DETAIL_SUFFIX}`

export const buildBusinessOrderDetailPathFromToken = (token: string) =>
  `${VIEW_PATHS.business}/pedidos/${encodeURIComponent(token.trim())}/${ACCOUNT_DETAIL_SUFFIX}`

export const buildBusinessQuoteDetailPathFromToken = (token: string) =>
  `${VIEW_PATHS.business}/orcamentos/${encodeURIComponent(token.trim())}/${ACCOUNT_DETAIL_SUFFIX}`

export const buildQuoteDetailPathFromToken = (token: string) =>
  `${VIEW_PATHS.quotes}/${encodeURIComponent(token.trim())}/${ACCOUNT_DETAIL_SUFFIX}`

export const buildAttendanceDetailPathFromToken = (token: string) =>
  `${VIEW_PATHS.attendances}/${encodeURIComponent(token.trim())}/${ACCOUNT_DETAIL_SUFFIX}`

export const buildOrderDetailPath = (orderId: number, detailKey?: string | number | null) =>
  buildOrderDetailPathFromToken(buildOrderDetailToken(orderId, detailKey))

export const buildQuoteDetailPath = (quoteId: number, detailKey?: string | number | null) =>
  buildQuoteDetailPathFromToken(buildQuoteDetailToken(quoteId, detailKey))

export const buildAttendanceDetailPath = (attendanceId: number, detailKey?: string | number | null) =>
  buildAttendanceDetailPathFromToken(buildAttendanceDetailToken(attendanceId, detailKey))

export const resolveAccountDetailIdFromPathSegment = (segment: string | undefined) => {
  if (!segment) return null
  let normalized = segment.trim()
  try {
    normalized = decodeURIComponent(normalized).trim()
  } catch {
    normalized = normalized.trim()
  }
  if (!normalized) return null

  const exactMatch = normalized.match(/^\d+$/)
  if (exactMatch) {
    const parsed = Number.parseInt(normalized, 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null
  }

  const prefixedMatch = normalized.match(/^(\d+)(?:-|$)/)
  if (!prefixedMatch) return null

  const parsed = Number.parseInt(prefixedMatch[1], 10)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null
}

const isModifiedEvent = (event: MouseEvent<HTMLElement>) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey

const isPlainLeftClick = (event: MouseEvent<HTMLElement>) =>
  !event.defaultPrevented && event.button === 0 && !isModifiedEvent(event)

export const handleLinkClick = (
  event: MouseEvent<HTMLElement>,
  handler: () => void
) => {
  if (!isPlainLeftClick(event)) return
  event.preventDefault()
  handler()
}
